import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, SlidersHorizontal } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { bn, fmtDate } from "../lib/format";
import { Empty, Loader, Modal, Field, GlassPage, GlassHeader } from "../components/kit";
import { useDebounced } from "../components/ProductSearch";

const REASONS = ["নতুন মাল যোগ", "ড্যামেজ / নষ্ট", "হারানো", "ফেরত", "গণনায় ভুল সংশোধন", "অন্যান্য"];
const TYPE: Record<string, [string, string]> = {
  sale: ["বিক্রি", "bg-blue-500/20 text-blue-300"],
  purchase: ["ক্রয়", "bg-emerald-500/20 text-emerald-300"],
  adjust: ["সমন্বয়", "bg-amber-500/20 text-amber-300"],
  opening: ["শুরু", "bg-slate-400/20 text-slate-200"],
};

function GBadge({ cls, children }: { cls: string; children: React.ReactNode }) {
  return <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>{children}</span>;
}

function AdjustForm({ p, onDone }: { p: any; onDone: () => void }) {
  const [dir, setDir] = useState("+");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/stock/adjust", { product_id: p.id, change: (dir === "+" ? 1 : -1) * (+qty || 0), reason });
      toast.success("স্টক আপডেট হয়েছে");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="stock-adjust-form">
      <p className="text-sm">বর্তমান স্টক: <b className="num">{bn(p.stock)} {p.unit}</b></p>
      <div className="grid grid-cols-2 gap-2">
        {["+", "-"].map((d) => (
          <button type="button" key={d} onClick={() => setDir(d)} data-testid={`stock-adjust-dir-${d === "+" ? "add" : "remove"}`}
            className={`btn ${dir === d ? (d === "+" ? "btn-green" : "bg-red-600 text-white") : "btn-ghost"}`}>{d === "+" ? "যোগ (+)" : "কমান (−)"}</button>
        ))}
      </div>
      <Field label="পরিমাণ"><input className="inp num" type="number" step="any" min="0" required value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-adjust-qty-input" autoFocus /></Field>
      <Field label="কারণ">
        <select className="inp" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="stock-adjust-reason-select">{REASONS.map((r) => <option key={r}>{r}</option>)}</select>
      </Field>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="stock-adjust-submit">সেভ করুন</button>
    </form>
  );
}

export default function Stock() {
  const [tab, setTab] = useState("list");
  const [q, setQ] = useState("");
  const [low, setLow] = useState(false);
  const [list, setList] = useState<any[] | null>(null);
  const [moves, setMoves] = useState<any[] | null>(null);
  const [adj, setAdj] = useState<any>(null);
  const dq = useDebounced(q);

  const load = useCallback(() => {
    api.get("/products", { params: { q: dq, low } }).then((r) => setList(r.data));
  }, [dq, low]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "moves") api.get("/stock/movements").then((r) => setMoves(r.data));
  }, [tab]);

  const tabBtn = (id: string, label: string, tid: string) => (
    <button
      className={`inline-flex items-center h-9 px-4 rounded-lg text-sm font-semibold transition-all active:scale-95 ${tab === id ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25" : "bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10"}`}
      onClick={() => setTab(id)} data-testid={tid}>{label}</button>
  );

  return (
    <GlassPage testid="stock-page">
      <GlassHeader title="স্টক" subtitle="ক্রয়ে স্টক বাড়ে, বিক্রিতে কমে — দরকার হলে নিজে সমন্বয় করুন" />
      <div className="flex gap-2 mb-4">
        {tabBtn("list", "স্টক তালিকা", "stock-tab-list")}
        {tabBtn("moves", "স্টক আসা-যাওয়া", "stock-tab-movements")}
      </div>

      {tab === "list" ? (
        <>
          <div className="glass-card p-4 flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="ginp pl-10" placeholder="পণ্য খুঁজুন" value={q} onChange={(e) => setQ(e.target.value)} data-testid="stock-search-input" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium px-2 text-slate-200">
              <input type="checkbox" className="w-4 h-4 accent-emerald-500" checked={low} onChange={(e) => setLow(e.target.checked)} data-testid="stock-low-filter" /> শুধু কম স্টক
            </label>
          </div>
          <div className="glass-card overflow-x-auto">
            {!list ? <Loader /> : list.length === 0 ? <Empty /> : (
              <table className="w-full text-sm" data-testid="stock-table">
                <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">পণ্য</th><th className="px-3 py-3 font-medium">কোম্পানি</th><th className="px-3 py-3 font-medium text-right">স্টক</th><th className="px-3 py-3 font-medium">অবস্থা</th><th className="px-5 py-3" /></tr></thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-5 py-3 font-semibold text-white">{p.name}</td>
                      <td className="px-3 py-3 text-slate-300">{p.company || "—"}</td>
                      <td className="px-3 py-3 text-right num font-bold text-white" data-testid={`stock-qty-${p.id}`}>{bn(p.stock)} {p.unit}</td>
                      <td className="px-3 py-3">{p.stock <= 0 ? <GBadge cls="bg-rose-500/20 text-rose-300">শেষ</GBadge> : p.stock <= p.low_stock ? <GBadge cls="bg-amber-500/20 text-amber-300">কম</GBadge> : <GBadge cls="bg-emerald-500/20 text-emerald-300">ঠিক আছে</GBadge>}</td>
                      <td className="px-5 py-3 text-right"><button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 text-sm font-semibold transition-all active:scale-95" onClick={() => setAdj(p)} data-testid={`stock-adjust-button-${p.id}`}><SlidersHorizontal size={15} /> সমন্বয়</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="glass-card overflow-x-auto">
          {!moves ? <Loader /> : moves.length === 0 ? <Empty /> : (
            <table className="w-full text-sm" data-testid="stock-movements-table">
              <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">তারিখ</th><th className="px-3 py-3 font-medium">পণ্য</th><th className="px-3 py-3 font-medium">ধরন</th><th className="px-3 py-3 font-medium">রেফারেন্স</th><th className="px-5 py-3 font-medium text-right">পরিমাণ</th></tr></thead>
              <tbody>
                {moves.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-5 py-3 num text-slate-300">{fmtDate(m.date)}</td>
                    <td className="px-3 py-3 text-slate-100">{m.product_name}</td>
                    <td className="px-3 py-3"><GBadge cls={TYPE[m.type]?.[1] || "bg-slate-400/20 text-slate-200"}>{TYPE[m.type]?.[0] || m.type}</GBadge></td>
                    <td className="px-3 py-3 text-slate-400">{m.ref}</td>
                    <td className={`px-5 py-3 text-right num font-bold ${m.change < 0 ? "text-rose-300" : "text-emerald-300"}`}>{m.change > 0 ? "+" : ""}{bn(m.change)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Modal open={!!adj} onClose={() => setAdj(null)} title={`স্টক সমন্বয়: ${adj?.name || ""}`} testid="stock-adjust-modal">
        {adj && <AdjustForm p={adj} onDone={() => { setAdj(null); load(); }} />}
      </Modal>
    </GlassPage>
  );
}
